/**
 * Dev-only HUD für den Travel-Stress-Test.
 * Zeigt Daten (Routen, Schiffe, Status, FPS min/avg/max) und Buttons zum Fokussieren.
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { TravelStressTest } from '../systems/TravelStressTest';
import { MAX_SHIPS_PER_FLEET } from '../systems/TravelStressTest';

const FPS_SAMPLE_WINDOW = 120; // frames to track for min/avg/max

export class StressTestHUD implements GameModule {
  readonly name = 'StressTestHUD';

  private _el!: HTMLDivElement;
  private _engine!: Engine;
  private _stressTest: TravelStressTest;
  private _dataEl!: HTMLDivElement;
  private _fpsEl!: HTMLDivElement;
  private _linesBtn!: HTMLButtonElement;
  private _timer = 0;

  // FPS tracking
  private readonly _fpsSamples = new Float32Array(FPS_SAMPLE_WINDOW);
  private _sampleIdx = 0;
  private _sampleCount = 0;
  private _fpsMin = Infinity;
  private _fpsMax = 0;
  private _fpsAvg = 0;

  constructor(stressTest: TravelStressTest) {
    this._stressTest = stressTest;
  }

  init(engine: Engine): void {
    this._engine = engine;
    this._el = document.createElement('div');
    Object.assign(this._el.style, {
      position: 'fixed',
      bottom: '8px',
      left: '8px',
      color: '#8cf',
      fontFamily: 'monospace',
      fontSize: 'clamp(0.6rem,1.8vw,0.75rem)',
      background: 'rgba(0,20,40,0.85)',
      padding: 'clamp(4px,1vw,8px) clamp(8px,1.5vw,12px)',
      borderRadius: '6px',
      zIndex: '9999',
      border: '1px solid rgba(100,180,255,0.3)',
    });

    this._dataEl = document.createElement('div');
    this._dataEl.style.marginBottom = '4px';

    this._fpsEl = document.createElement('div');
    this._fpsEl.style.marginBottom = '8px';

    const btnRow1 = document.createElement('div');
    btnRow1.style.display = 'flex';
    btnRow1.style.gap = '6px';
    btnRow1.style.flexWrap = 'wrap';
    btnRow1.style.marginBottom = '6px';

    this._btn('Start 20', () => this._stressTest.start(20), btnRow1);
    this._btn('Start 50', () => this._stressTest.start(50), btnRow1);
    this._btn('Start 100', () => this._stressTest.start(100), btnRow1);
    this._btn('Stop', () => this._stressTest.stop(), btnRow1);

    const btnRow2 = document.createElement('div');
    btnRow2.style.display = 'flex';
    btnRow2.style.gap = '6px';
    btnRow2.style.flexWrap = 'wrap';

    this._linesBtn = this._btn('Linien aus', () => this._toggleLines(), btnRow2);
    this._btn('Fly to Center', () => this._flyToCenter(), btnRow2);
    this._btn('Route 0', () => this._flyToRoute(0), btnRow2);
    this._btn('Route 1', () => this._flyToRoute(1), btnRow2);
    this._btn('Route 2', () => this._flyToRoute(2), btnRow2);
    this._btn('Route 5', () => this._flyToRoute(5), btnRow2);
    this._btn('Route 10', () => this._flyToRoute(10), btnRow2);

    this._el.append(this._dataEl, this._fpsEl, btnRow1, btnRow2);
    document.body.appendChild(this._el);
  }

  private _btn(label: string, onClick: () => void, parent: HTMLElement): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    Object.assign(b.style, {
      padding: '4px 8px',
      fontSize: '11px',
      fontFamily: 'monospace',
      background: 'rgba(50,100,150,0.6)',
      color: '#bef',
      border: '1px solid rgba(100,180,255,0.5)',
      borderRadius: '4px',
      cursor: 'pointer',
    });
    b.addEventListener('click', onClick);
    parent.appendChild(b);
    return b;
  }

  private _toggleLines(): void {
    this._stressTest.setShowLines(!this._stressTest.showLines);
  }

  private _flyToCenter(): void {
    const pos = this._stressTest.getCenterOfAllRoutes();
    if (pos) this._engine.events.emit('stress:focus-camera', pos);
  }

  private _flyToRoute(index: number): void {
    const pos = this._stressTest.getRouteCenter(index);
    if (pos) this._engine.events.emit('stress:focus-camera', pos);
  }

  private _updateFpsSamples(): void {
    const fps = this._engine.clock.fps;
    this._fpsSamples[this._sampleIdx] = fps;
    this._sampleIdx = (this._sampleIdx + 1) % FPS_SAMPLE_WINDOW;
    if (this._sampleCount < FPS_SAMPLE_WINDOW) this._sampleCount++;

    let min = Infinity, max = 0, sum = 0;
    for (let i = 0; i < this._sampleCount; i++) {
      const v = this._fpsSamples[i];
      if (v < min) min = v;
      if (v > max) max = v;
      sum += v;
    }
    this._fpsMin = min;
    this._fpsMax = max;
    this._fpsAvg = sum / this._sampleCount;
  }

  private _resetFpsTracking(): void {
    this._sampleIdx = 0;
    this._sampleCount = 0;
    this._fpsMin = Infinity;
    this._fpsMax = 0;
    this._fpsAvg = 0;
  }

  update(dt: number): void {
    const s = this._stressTest.state;

    // Always sample FPS when stress test is active
    if (s.active) {
      this._updateFpsSamples();
    } else if (this._sampleCount > 0 && !s.active) {
      this._resetFpsTracking();
    }

    this._timer += dt;
    if (this._timer < 0.3) return;
    this._timer = 0;

    const totalShips = s.routeCount * MAX_SHIPS_PER_FLEET;
    let text = `STRESS: ${s.routeCount} Routen · ${totalShips} Schiffe`;
    if (s.loading) text += ' · LADE…';
    else if (s.active) text += ` · AKTIV · sichtbar: ${s.visibleFleets}`;
    else text += ' · Ctrl+Shift+S';
    this._dataEl.textContent = text;

    if (s.active && this._sampleCount > 10) {
      const col = this._fpsMin >= 55 ? '#0f0' : this._fpsMin >= 30 ? '#ff0' : '#f00';
      this._fpsEl.style.color = col;
      this._fpsEl.textContent =
        `FPS min ${this._fpsMin.toFixed(0)} · avg ${this._fpsAvg.toFixed(0)} · max ${this._fpsMax.toFixed(0)}` +
        ` · ${(1000 / this._fpsAvg).toFixed(1)}ms`;
    } else {
      this._fpsEl.textContent = '';
    }

    this._linesBtn.textContent = this._stressTest.showLines ? 'Linien an' : 'Linien aus';
  }

  dispose(): void {
    this._el.remove();
  }
}
